/**
 * Per-burst impact frame: surface normal, two tangents, and an up-blend.
 *
 * Floor hits (normal ≈ +Y) keep the existing world-up mushroom model.
 * Wall hits (normal ⊥ up) eject along the normal, splash in the wall plane,
 * and skip buoyancy. Slopes interpolate via upFactor = saturate(n·up).
 */

export interface ImpactFrame {
  readonly normal: readonly [number, number, number];
  readonly tangent: readonly [number, number, number];
  readonly bitangent: readonly [number, number, number];
  readonly upFactor: number;
}

export const DEFAULT_SURFACE_NORMAL: readonly [number, number, number] = [0, 1, 0];

/** Wall hits keep this fraction of the mushroom / vortex-ring terms. */
export const WALL_BUOYANCY_SCALE = 0.1;

/** Slow fines within this distance of a steep SDF face get the curtain slide. */
export const CURTAIN_DISTANCE = 0.5;

/** Fraction of wall-path grains/fines that spawn as an in-plane splash ring. */
export const WALL_SPLASH_FRACTION = 0.32;

export function clamp01(x: number): number {
  return Math.min(1, Math.max(0, x));
}

export function normalize3(
  v: readonly [number, number, number],
): [number, number, number] {
  const len = Math.hypot(v[0], v[1], v[2]);
  if (len < 1e-8) return [0, 1, 0];
  return [v[0] / len, v[1] / len, v[2] / len];
}

export function dot3(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

export function cross3(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
): [number, number, number] {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

/**
 * Orthonormal tangent/bitangent for `dir`. Same construction the spawn
 * planners have always used (world-up hint, fallback to +X).
 */
export function orthoFrame(dir: readonly [number, number, number]): {
  t: [number, number, number];
  b: [number, number, number];
} {
  const up: [number, number, number] = Math.abs(dir[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
  const t = cross3(up, dir);
  const tLen = Math.hypot(t[0], t[1], t[2]) || 1;
  t[0] /= tLen;
  t[1] /= tLen;
  t[2] /= tLen;
  const b = cross3(dir, t);
  return { t, b };
}

/** Uniform cone around `dir`. `spreadRad` is the half-angle. */
export function coneAround(
  rng: () => number,
  dir: readonly [number, number, number],
  spreadRad: number,
): [number, number, number] {
  const { t, b } = orthoFrame(dir);
  const theta = spreadRad * Math.sqrt(rng());
  const phi = rng() * Math.PI * 2;
  const st = Math.sin(theta);
  const ct = Math.cos(theta);
  const cp = Math.cos(phi);
  const sp = Math.sin(phi);
  return [
    dir[0] * ct + t[0] * st * cp + b[0] * st * sp,
    dir[1] * ct + t[1] * st * cp + b[1] * st * sp,
    dir[2] * ct + t[2] * st * cp + b[2] * st * sp,
  ];
}

export function upFactorFromNormal(
  normal: readonly [number, number, number],
): number {
  return clamp01(normalize3(normal)[1]);
}

export function deriveImpactFrame(
  normal: readonly [number, number, number] = DEFAULT_SURFACE_NORMAL,
): ImpactFrame {
  const n = normalize3(normal);
  const { t, b } = orthoFrame(n);
  return {
    normal: n,
    tangent: t,
    bitangent: b,
    upFactor: clamp01(n[1]),
  };
}

/** Restitution-style reflection of an incoming unit vector about `normal`. */
export function reflectIncoming(
  incoming: readonly [number, number, number],
  normal: readonly [number, number, number],
): [number, number, number] {
  const i = normalize3(incoming);
  const n = normalize3(normal);
  const d = 2 * dot3(i, n);
  return normalize3([i[0] - d * n[0], i[1] - d * n[1], i[2] - d * n[2]]);
}

export function mix3(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  t: number,
): [number, number, number] {
  const u = clamp01(t);
  return [
    a[0] + (b[0] - a[0]) * u,
    a[1] + (b[1] - a[1]) * u,
    a[2] + (b[2] - a[2]) * u,
  ];
}
